import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { orgDashboardApiIsomorphicFetchOrg } from '#/modules/orgDashboard/api/orgDashboardApiFetchOrg';
import { orgDashboardManageListingsRoute } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import { orgDashboardManageListingsIsomorphicSearch } from '#/modules/orgDashboard/utils/orgDashboardManageListingsIsomorphicSearch';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';

import { OrgDashboardManageListingsPageLoadable } from './OrgDashboardManageListingsPageLoadable';

export const handle = {
  fallbackUrlByLocaleToCurrent: true,
  darkBackground: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteLoaderArgs<typeof orgDashboardManageListingsRoute>) {
  const searchParams = getAppRouteSearchParams({
    url: request.url,
    route: orgDashboardManageListingsRoute,
  });

  const pageIndex = parseInt(searchParams.page ?? '1', 10) - 1;
  const status = searchParams.status ?? 'ALL';
  const query = searchParams.query ?? '';
  const type = searchParams.type ?? 'ALL';

  const [{ appConfig }, { org, user: orgUserPreferences }] = await Promise.all([
    apiIsomorphicFetchUserEnvironment({ request, context })(),
    orgDashboardApiIsomorphicFetchOrg({
      request,
      context,
    })(orgId),
    OrgDashboardManageListingsPageLoadable.load(),
  ]);

  const resultsPromise = orgDashboardManageListingsIsomorphicSearch({
    searchApiKey: org.searchApiKey,
    type,
    query,
    pageIndex,
    status,
    isUsingAlgoliaMock: appConfig.algolia.mock,
  });

  return {
    preferredListingTypes: orgUserPreferences.preferredListingTypes ?? [],
    query,
    pageIndex,
    status,
    type,
    resultsPromise,
  };
}

export default function OrgDashboardManageListingsHorizonRoute({
  loaderData: {
    preferredListingTypes,
    query,
    pageIndex,
    status,
    type,
    resultsPromise,
  },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  return (
    <RouteModule title={i18n('Manage Listings')}>
      <OrgDashboardManageListingsPageLoadable
        preferredListingTypes={preferredListingTypes}
        query={query}
        pageIndex={pageIndex}
        status={status}
        type={type}
        resultsPromise={resultsPromise}
      />
    </RouteModule>
  );
}
